import React, { useState } from 'react';

const CategoryManagement = ({ categories, items, onAddCategory, onDeleteCategory }) => {
  const [newCategory, setNewCategory] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(null);

  const handleAdd = async (e) => {
    e.preventDefault();
    const trimmed = newCategory.trim();
    if (!trimmed) {
      setError('Category name cannot be empty');
      return;
    }
    if (categories.includes(trimmed)) {
      setError('Category already exists');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await onAddCategory(trimmed);
      setNewCategory('');
    } catch (err) {
      setError('Failed to add category');
    } finally {
      setSaving(false);
    }
  };

  const getItemCount = (category) => {
    return items.filter(item => item.category === category).length;
  };

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Category Management</h1>

      {/* Add Category Form */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold mb-4">Add Category</h2>
        <form onSubmit={handleAdd} className="flex gap-3">
          <input
            type="text"
            value={newCategory}
            onChange={(e) => { setNewCategory(e.target.value); setError(''); }}
            className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter category name"
          />
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Adding...' : 'Add Category'}
          </button>
        </form>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </div>

      {/* Categories Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {categories.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-500">No categories yet. Add your first category above.</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Items
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {categories.map(category => {
                const count = getItemCount(category);
                return (
                  <tr key={category} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{category}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{count} item{count !== 1 ? 's' : ''}</td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={async () => {
                          if (count > 0) {
                            if (!window.confirm(`"${category}" is used by ${count} item(s). Delete anyway?`)) return;
                          }
                          setDeletingCategory(category);
                          try {
                            await onDeleteCategory(category);
                          } finally {
                            setDeletingCategory(null);
                          }
                        }}
                        disabled={deletingCategory === category}
                        className="text-red-600 hover:text-red-900 text-sm disabled:opacity-50"
                      >
                        {deletingCategory === category ? 'Deleting...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CategoryManagement;
